import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { apiClient } from '../../../services/apiClient';
import { useToast } from "../../../Toaster/ToastContext";
import { useSelector } from 'react-redux';
import { saveHoldOrder } from '../../../services/holdService';

const HoldModal = ({ isOpen, onClose, orderItems, tax, clearOrder, customerId = null }) => {
    const { showToast } = useToast();
    const [orderReference, setOrderReference] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const selectedBranch = useSelector((state) => state.selectedBranch);

    if (!isOpen) return null;

    // Calculate subtotal (before discounts), total discount, tax, and final total
    const subtotal = orderItems.reduce((sum, item) => sum + item.price * (item.quantity ?? 1), 0).toFixed(2);
    const totalDiscount = orderItems.reduce((sum, item) => sum + (item.discountAmount || 0) * (item.quantity ?? 1), 0).toFixed(2);
    const subtotalAfterDiscount = orderItems.reduce((sum, item) => sum + (item.discountedPrice || item.price) * (item.quantity ?? 1), 0).toFixed(2);
    const taxAmount = parseFloat(tax).toFixed(2); // Tax is a flat amount
    const totalAmount = (parseFloat(subtotalAfterDiscount) + parseFloat(taxAmount)).toFixed(2);

    const handleConfirmHold = async (e) => {
        e.preventDefault();

        if (!orderItems.length) {
            showToast("No items in the order to hold.", 'error', "Hold Order");
            return;
        }

        setIsSubmitting(true);

        const holdOrder = {
            branch_id: selectedBranch?.id,
            customer_id: customerId,
            items: orderItems.map(item => ({
                product_id: String(item.id).split('-')[0], // Extract base product ID
                quantity: item.quantity ?? 1,
                price: item.discountedPrice || item.price,
                variant_id: item.variantId || null,
            })),
            subtotal: parseFloat(subtotal),
            total_discount: parseFloat(totalDiscount),
            tax: parseFloat(taxAmount),
            total: parseFloat(totalAmount),
            status: 'hold',
            note: orderReference,
        };

        try {
            const response = await apiClient.post('/store-sale', holdOrder);
            if (response?.status) {
                showToast(response.message || "Order placed on hold successfully!", 'success', "Hold Order");
                setOrderReference('');
                clearOrder();
                onClose();
            } else {
                throw new Error(response.message || "Failed to hold order.");
            }
        } catch (err) {
            console.warn("API failed, saving hold locally:", err);
            await saveHoldOrder(holdOrder);
            clearOrder();
            onClose();
            showToast("Saved locally due to network/API issue.", 'info', "Hold Order");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="modal fade pos-modal show" id="hold-order" tabIndex="-1" style={{ display: 'block' }} aria-modal="true" role="dialog">
            <div className="modal-dialog modal-dialog-centered modal-md">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Hold Order</h5>
                        <button type="button" className="close" onClick={onClose}><span>×</span></button>
                    </div>
                    <form onSubmit={handleConfirmHold}>
                        <div className="modal-body">
                            <div className="bg-light br-10 p-4 text-center mb-3">
                                <h2 className="display-1">PKR {totalAmount}</h2>
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Order Reference</label>
                                <input
                                    className="form-control"
                                    type="text"
                                    value={orderReference}
                                    onChange={(e) => setOrderReference(e.target.value)}
                                    placeholder="Enter order reference"

                                />
                            </div>
                            <p>This order will be held and can be resumed from the “Pending Orders” list.</p>
                        </div>
                        <div className="modal-footer d-flex justify-content-end gap-2">
                            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={isSubmitting}>
                                Cancel
                            </button>
                            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                                {isSubmitting ? 'Saving...' : 'Confirm'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

HoldModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    orderItems: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired, // Allow string or number
        name: PropTypes.string.isRequired,
        price: PropTypes.number.isRequired,
        quantity: PropTypes.number, // Made optional
        variantId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        discount_type: PropTypes.string,
        discount_value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        discountedPrice: PropTypes.number,
        discountAmount: PropTypes.number,
    })).isRequired,
    tax: PropTypes.number.isRequired,
    clearOrder: PropTypes.func.isRequired,
    customerId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default HoldModal;